/**
 * QuickAddStrainModal Component
 *
 * Minimal modal for quickly adding a new strain when one doesn't exist.
 * Used when adding a third-party syringe with a strain not yet in the system.
 *
 * Required fields: Name, Code
 * Optional fields: Scientific name (binomial)
 */

import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useStrainMutations } from '@/hooks/useStrains'
import { toast } from 'sonner'
import { Dna, Loader2 } from 'lucide-react'

/**
 * QuickAddStrainModal Component
 *
 * @param {Object} props
 * @param {boolean} props.open - Dialog open state
 * @param {Function} props.onOpenChange - Callback when dialog state changes
 * @param {Function} props.onSuccess - Callback on successful create, receives created strain
 * @param {string} props.initialName - Optional initial name to pre-fill
 */
export default function QuickAddStrainModal({ open, onOpenChange, onSuccess, initialName = '' }) {
  const { createStrain, updating } = useStrainMutations()

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    scientific_name: '',
  })

  const [errors, setErrors] = useState({})

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setFormData({
        name: initialName,
        code: '',
        scientific_name: '',
      })
      setErrors({})
    }
  }, [open, initialName])

  // Auto-generate code from name (first 2-3 characters uppercase)
  const generateCodeFromName = (name) => {
    if (!name) return ''
    // Take first word and use first 2-3 chars
    const firstWord = name.trim().split(' ')[0]
    if (firstWord.length <= 2) return firstWord.toUpperCase()
    // Use 2 chars for short names, 3 for longer
    const chars = firstWord.length <= 4 ? 2 : 3
    return firstWord.substring(0, chars).toUpperCase()
  }

  const validateForm = () => {
    const newErrors = {}

    // Name is required
    if (!formData.name.trim()) {
      newErrors.name = 'Strain name is required'
    }

    // Code is required
    if (!formData.code.trim()) {
      newErrors.code = 'Strain code is required'
    } else if (!/^[A-Z0-9]{2,6}$/.test(formData.code.trim().toUpperCase())) {
      newErrors.code = 'Code must be 2-6 alphanumeric characters'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value }

      // Auto-generate code when name changes and code is empty or was auto-generated
      if (field === 'name' && (!prev.code || prev.code === generateCodeFromName(prev.name))) {
        updated.code = generateCodeFromName(value)
      }

      return updated
    })

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined,
      }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      const strainData = {
        name: formData.name.trim(),
        code: formData.code.trim().toUpperCase(),
        scientific_name: formData.scientific_name.trim() || null,
        // Set defaults for a new strain
        category: 'gourmet',
        active: true,
      }

      const { data, error } = await createStrain(strainData)

      if (error) {
        // Check for unique constraint violation
        if (error.code === '23505') {
          if (error.message.includes('code')) {
            setErrors({ code: 'A strain with this code already exists' })
          } else {
            setErrors({ name: 'A strain with this name already exists' })
          }
          return
        }
        throw error
      }

      toast.success('Strain created', {
        description: `${data.name} (${data.code}) has been added.`,
      })

      onSuccess?.(data)
    } catch (err) {
      console.error('Error creating strain:', err)
      toast.error('Error creating strain', {
        description: err.message,
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Dna className="h-5 w-5" />
            Quick Add Strain
          </DialogTitle>
          <DialogDescription>
            Add a new strain to the system. You can add more details later on the strain&apos;s detail page.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Strain Name */}
          <div className="space-y-2">
            <Label htmlFor="strain-name">
              Strain Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="strain-name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="e.g., Lion's Mane"
              className={errors.name ? 'border-red-500' : ''}
              autoFocus
            />
            {errors.name && <p className="text-red-500 text-sm">{errors.name}</p>}
          </div>

          {/* Strain Code */}
          <div className="space-y-2">
            <Label htmlFor="strain-code">
              Strain Code <span className="text-red-500">*</span>
            </Label>
            <Input
              id="strain-code"
              value={formData.code}
              onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
              placeholder="e.g., LM"
              maxLength={6}
              className={errors.code ? 'border-red-500' : ''}
            />
            <p className="text-xs text-muted-foreground">
              2-6 character code used for batch identification
            </p>
            {errors.code && <p className="text-red-500 text-sm">{errors.code}</p>}
          </div>

          {/* Scientific Name (Binomial) */}
          <div className="space-y-2">
            <Label htmlFor="scientific-name">
              Scientific Name <span className="text-muted-foreground text-sm">(optional)</span>
            </Label>
            <Input
              id="scientific-name"
              value={formData.scientific_name}
              onChange={(e) => handleInputChange('scientific_name', e.target.value)}
              placeholder="e.g., Hericium erinaceus"
              className="italic"
            />
          </div>

          <DialogFooter className="gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={updating}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="create"
              disabled={updating}
            >
              {updating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Add Strain'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
